import React, { Fragment, lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TimelineIcon from "@mui/icons-material/Timeline";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import BallotIcon from "@mui/icons-material/Ballot";
import FilterNone from "@mui/icons-material/FilterNone";
import EventIcon from "@mui/icons-material/Event";
import PictureInPictureIcon from "@mui/icons-material/PictureInPicture";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import LabelImportantOutlinedIcon from "@mui/icons-material/LabelImportantOutlined";
import GroupWorkOutlinedIcon from "@mui/icons-material/GroupWorkOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

import ProjectLayout from "./layouts/ProjectLayout/index";
import ShoppingLayout from "./layouts/ShoppingLayout/index";
import LoadingScreen from "./components/LoadingScreen";
import DashboardGuard from "./guards/DashboardGuard";
import GuestGuard from "./guards/GuestGuard";
import InvitationGuard from "./guards/InvitationGuard";
import ShoppingGuard from "./guards/ShoppingGuard";
import CanGuard from "./guards/CanGuard";
import InsightsGuard from "./guards/InsightsGuard";

const toRelativePath = (path, parentPath) => {
  if (!parentPath || !path) return path;
  if (parentPath === "/") {
    return path.startsWith("/") ? path.slice(1) : path;
  }
  if (path === parentPath) return "";
  if (path.startsWith(parentPath + "/")) return path.slice(parentPath.length + 1);
  return path;
};

export const renderRoutes = (routes = [], parentPath = "") => (
  <Suspense fallback={<LoadingScreen />}>
    <Routes>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;
        const guardProps = route.guard ? { can: route.can } : {};
        const paths = Array.isArray(route.path) ? route.path : [route.path];

        return paths.map((path, j) => {
          const relative = toRelativePath(path, parentPath);
          const routePath = route.routes
            ? `${relative.replace(/\/$/, "")}/*`
            : relative;

          return (
            <Route
              key={`${i}-${j}`}
              path={routePath}
              element={
                <Guard {...guardProps}>
                  <Layout>
                    {route.routes ? renderRoutes(route.routes, path) : <Component />}
                  </Layout>
                </Guard>
              }
            />
          );
        });
      })}
    </Routes>
  </Suspense>
);

const routes = [
  {
    path: "/404",
    component: lazy(() => import("src/layouts/errors/NotFoundView")),
  },
  {

    path: "/403",
    component: lazy(() => import("src/layouts/errors/PermissionDeniedView")),
  },
  {

    guard: GuestGuard,
    path: ["/login", "/choose-workspace"],
    component: lazy(() => import("src/views/auth/LoginView")),
  },
  {

    guard: InvitationGuard,
    path: "/invitation",
    component: lazy(() => import("src/views/auth/InvitationView")),
  },
  {

    path: "/reset-password",
    component: lazy(() => import("src/views/auth/ResetPasswordView")),
  },
  {

    guard: GuestGuard,
    path: "/forgot-password",
    component: lazy(() => import("src/views/auth/ForgetPasswordView")),
  },
  {

    guard: GuestGuard,
    path: "/shared-planner/:token",
    component: lazy(() => import("src/views/planner/sharedView")),
  },
  {
    path: "/settings/*",
    component: lazy(() => import("src/views/settings/Settings")),
  },
  {
    path: "/shopping",
    guard: ShoppingGuard,
    layout: ShoppingLayout,
    routes: [
      {
    
        path: ["/shopping/profile", "/shopping/profile/security"],
        component: lazy(() => import("./views/shopping/ShoppingProfile/ShoppingProfile")),
      },
      {
    
        path: "/shopping/cart/submitted",
        component: lazy(() => import("src/views/shopping/Cart/SubmittedPage")),
      },
      {
    
        path: "/shopping/cart/:section?",
        component: lazy(() => import("src/views/shopping/Cart/Page")),
      },
      {
    
        path: [
          "/shopping/reservations",
          "/shopping/orders",
          "/shopping/calendar",
          "/shopping/internal",
          "/shopping/reports",
        ],
        component: lazy(() => import("src/views/shopping/Orders/Page")),
      },
      {

        path: ["/shopping", "/shopping/:projectId"],
        component: lazy(() => import("src/views/shopping/ProjectAssets/Page")),
      },
    ],
  },
  {
    path: "/deliveries/:uid/signature/sign",
    guard: DashboardGuard,
    component: lazy(() =>
      import("src/views/deliveries/delivery-guided-wizard/Signature/DeliverySignature")
    ),
  },
  {
    path: "/deliveries/:uid/signature",
    guard: DashboardGuard,
    component: lazy(() =>
      import("src/views/deliveries/delivery-guided-wizard/Signature/DeliverySignature")
    ),
  },
  {
    path: "/",
    guard: DashboardGuard,
    layout: ProjectLayout,
    routes: [
      {
    
        path: "/dashboard",
        component: lazy(() => import("src/views/root/Page")),
      },
      {
    
        path: "/review",
        component: lazy(() => import("src/views/review/Page")),
      },
      {
        path: "/shipment-approval",
        component: lazy(() => import("src/views/review/ShipmentApprovalPage")),
      },
      {
        path: "/shipment-approval-complete",
        component: lazy(() => import("src/views/review/ShipmentApprovalCompletePage")),
      },
      {
    
        path: "/overview",
        component: lazy(() => import("src/views/overview/Page")),
      },
      {
    
        path: "/kitting",
        component: lazy(() => import("src/views/kitting/Kitting")),
      },
      {
    
        path: ["/labels", "/projects/:projectId/labels", "/clients/:clientId/labels"],
        component: lazy(() => import("src/views/labels/index")),
      },
      {
    
        path: "/notifications",
        component: lazy(() => import("src/views/notifications/Page")),
      },
      {
    
        path: "/profile",
        component: lazy(() => import("src/views/users/PageProfile")),
      },
      {
    
        path: "/profile/:tab",
        component: lazy(() => import("src/views/users/PageProfile")),
      },
      {
        path: "/inventory-move-search",
        component: lazy(() =>
          import("src/components/modals/MoveLocationsModal/InventoryMoveSearchRoute")
        ),
      },
      {
    
        path: "/clients",
        component: lazy(() => import("src/views/clients/Page")),
      },
      {
    
        path: ["/inventory", "/inventory/:view"],
        component: lazy(() => import("src/views/inventory/Page")),
        guard: CanGuard(["features.inventory_tracking"]),
      },
      {
    
        path: ["/calendar", "/calendar/:view"],
        component: lazy(() => import("src/views/inventory/Page")),
        guard: CanGuard(["features.inventory_tracking"]),
      },
      {
    
        path: "/client/:clientId/edit",
        component: lazy(() => import("src/views/clients/PageEdit")),
        guard: CanGuard(["clients.write"]),
      },
      {
    
        path: "/projects",
        component: lazy(() => import("src/views/projects/Page")),
      },
      {
    
        path: "/project/:projectId/edit",
        component: lazy(() => import("src/views/projects/PageEdit")),
        guard: CanGuard(["projects.write"]),
      },
      {
    
        path: "/users",
        component: lazy(() => import("src/views/users/Page")),
      },
      {
    
        path: "/user/:userId/edit",
        component: lazy(() => import("src/views/users/PageEdit")),
        guard: CanGuard(["users.write"]),
      },

      {
        path: "/dashboard/:projectId/inventory-management/:assetId",
        component: lazy(() => import("src/views/asset/Page")),
        guard: CanGuard(["features.capture_inventory"]),
      },
      {
    
        path: ["/projects/:projectId/templates/new", "/clients/:clientId/templates/new"],
        component: lazy(() => import("./views/favoriteReport/PageEdit")),
        guard: CanGuard(["features.reporting"]),
      },
      {
    
        path: [
          "/projects/:projectId/templates/duplicate/:reportId",
          "/clients/:clientId/templates/duplicate/:reportId",
        ],
        component: lazy(() => import("./views/favoriteReport/PageEdit")),
        guard: CanGuard(["features.reporting"]),
      },
      {
    
        path: ["/projects/:projectId/templates/:reportId"],
        component: lazy(() => import("src/views/favoriteReport/PageEdit")),
        guard: CanGuard(["features.reporting"]),
      },
      {
    
        path: "/assets/:assetId",
        component: lazy(() => import("src/views/asset/Page")),
        guard: CanGuard(["assets.read"]),
      },
      {
    
        path: "/activity",
        component: lazy(() => import("src/views/activity/Page")),
        guard: CanGuard(["assets.read"]),
      },
      {
    
        path: ["/projects/:projectId/:tab", "/clients/:clientId/:tab"],
        component: lazy(() => import("src/layouts/ProjectLayout/Page")),
      },
      {
    
        path: ["/warehouse", "/warehouse/:id/:view"],
        component: lazy(() => import("src/views/warehouse/Page")),
      },
      {
    
        path: "/permissions/*",
        component: lazy(() => import("src/views/permissionGrid/Page")),
      },
      {
    
        path: "/insights",
        component: lazy(() => import("src/views/inventoryStatistic/Page")),
        guard: InsightsGuard,
      },
      {
    
        path: "/insights/reports/:notificationId",
        component: lazy(() => import("src/views/inventoryStatistic/Page")),
        guard: InsightsGuard,
      },
      {
    
        path: "/insights/:id",
        component: lazy(() => import("src/views/inventoryStatistic/Page")),
        guard: InsightsGuard,
      },
      {
    
        path: "/planners",
        component: lazy(() => import("src/views/planner/list")),
        guard: CanGuard(["features.equipment_planner"]),
      },
      {
    
        path: "/planner/:id",
        component: lazy(() => import("src/views/planner/adminView")),
        guard: CanGuard(["features.equipment_planner"]),
      },
      {
        path: "/loading",
        component: () => null,
      },
      {
        path: "/release-notes",
        component: lazy(() => import("src/views/releaseNotes/Page")),
      },
      {
        path: "/deliveries/:uid?",
        component: lazy(() => import("src/views/deliveries/Page")),
      },
      {
        path: "*",
        component: () => <Navigate to="/404" replace />,
      },
    ],
  },
];

export const NAV_BAR_ITEMS = [
  {
    title: "Manage",
    list: [
      {
        title: "Inventory",
        to: "/dashboard",
        icon: DashboardIcon,
        canRules: ["pages.dashboard"],
      },
      {
        title: "Review",
        to: "/review",
        icon: FactCheckOutlinedIcon,
        canRules: ["settings.warehouse", "settings.receiving_wizard"],
        customPermissionCheck: (can, role) => {
          const isAllowedRole = ["admin", "manager"].includes(role);
          if (!isAllowedRole) {
            return false;
          }
          return can("settings.warehouse") && can("settings.receiving_wizard");
        },
      },
      {
        title: "Warehouse",
        to: "/warehouse",
        icon: WarehouseOutlinedIcon,
        canRules: ["settings.warehouse"],
        customPermissionCheck: (can, role) => {
          const isRestrictedRole = ["client", "employee", "shopper", "viewer"].includes(role);
          return can("settings.warehouse") && (!isRestrictedRole || can("warehouse.page"));
        },
      },
      {
        title: "Kitting",
        to: "/kitting",
        icon: GroupWorkOutlinedIcon,
        canRules: ["settings.kitting"],
        customPermissionCheck: (can, role) => {
          const isRestrictedRole = ["employee", "shopper", "viewer"].includes(role);
          if (isRestrictedRole) {
            return false;
          }
          // Hide kitting tab if receiving wizard is enabled
          if (can("settings.receiving_wizard")) {
            return false;
          }
          return can("settings.kitting");
        },
      },
    ],
  },
  {
    title: "Request",
    list: [
      {
        title: "Store",
        to: "/shopping",
        icon: ShoppingCartOutlinedIcon,
        canRules: [],
        customPermissionCheck: can =>
          (can("pages.shopping") || can("pages.shopping_cart")) &&
          can("settings.inventory_tracking"),
      },
    ],
  },
  {
    title: "Fulfillment",
    list: [
      {
        title: ["va.glimpseit.com", "va.glimpseware.com"].includes(window.location.host)
          ? "Task Orders"
          : "Orders",
        to: "/inventory/outstanding_orders",
        icon: BallotIcon,
        canRules: ["features.inventory_tracking"],
        customPermissionCheck: (can, role) => {
          const isRestrictedRole = ["employee", "shopper", "viewer"].includes(role);
          if (isRestrictedRole) {
            return false;
          }
          return can("features.inventory_tracking");
        },
      },
      {
        title: "Reservations",
        to: "/inventory/reserved",
        icon: FilterNone,
        canRules: ["features.inventory_tracking", "settings.reservations"],
        customPermissionCheck: (can, role) => {
          const isShopperOrEmployee = ["employee", "shopper"].includes(role);
          if (isShopperOrEmployee) {
            return false;
          }
          return can("features.inventory_tracking") && can("settings.reservations");
        },
      },
      {
        title: "Calendar",
        to: "/calendar/month",
        icon: EventIcon,
        canRules: ["features.inventory_tracking", "settings.calendar"],
        customPermissionCheck: (can, role) => {
          const isShopperOrEmployee = ["employee", "shopper"].includes(role);
          if (isShopperOrEmployee) {
            return false;
          }
          return can("features.inventory_tracking") && can("settings.calendar");
        },
      },
      {
        title: "Deliveries",
        to: "/deliveries",
        icon: LocalShippingOutlinedIcon,
        customPermissionCheck: (can, role) => {
          const isRestrictedRole = ["employee", "shopper", "viewer", "client"].includes(role);
          if (isRestrictedRole) return false;
          return can("settings.allow_delivery");
        },
      },
    ],
  },
  {
    title: "Reporting",
    list: [
      {
        title: "Overview",
        to: "/overview",
        icon: InsightsOutlinedIcon,
        canRules: ["pages.dashboard"],
        customPermissionCheck: (can) => {
          return can("pages.dashboard") && can("features.inventory_tracking");
        },
      },
      {
        title: window.location.host === "onemedical.glimpseit.com" ? "Reports" : "Insights",
        to: "/insights",
        icon: EqualizerIcon,
        canRules: [],
        customPermissionCheck: (can, role) => {
          const legacyRoleAllowed = ["admin", "super_admin", "manager", "inventory", "billing_user"].includes(role);
          return legacyRoleAllowed || can("insights.page");
        },
      },
    ],
  },
  {
    title: "Other",
    list: [
      {
        title: "Planner",
        to: "/planners",
        icon: PictureInPictureIcon,
        canRules: ["settings.equipment_planner"],
      },
      {
        title: "Labels",
        to: "/labels",
        icon: LabelImportantOutlinedIcon,
        canRules: ["settings.labels"],
      },
    ],
  },
];

export default routes;
